import { useMemo } from 'react';
import type { PersonDto } from '@betreuung/shared';

/** The minimum a component needs to render a person: a name and a colour. */
export interface PersonLike {
  name: string;
  colorIndex: number;
}

export interface PersonLookup {
  /** `undefined` for an id that no longer exists (deleted person). */
  get: (personId: number) => PersonDto | undefined;
  /** Name and colour, falling back gracefully for unknown ids. */
  describe: (personId: number) => PersonLike;
  all: PersonDto[];
  /**
   * Everyone care time may be assigned to. Anything offering a *choice* of
   * person must use this; anything rendering a person that an assignment
   * already names must use `get`/`describe` instead.
   */
  caregivers: PersonDto[];
}

/**
 * A view-only account is a person row, so it arrives with everyone else — who
 * has access is deliberately not a secret between the people sharing a
 * calendar. It just must never be offered as somebody care time can be given to.
 */
export function caregiversOf(persons: readonly PersonDto[]): PersonDto[] {
  return persons.filter((person) => person.isCaregiver);
}

export function usePersonLookup(persons: PersonDto[] | undefined): PersonLookup {
  return useMemo(() => {
    const list = persons ?? [];
    const byId = new Map(list.map((person) => [person.id, person]));
    return {
      all: list,
      caregivers: caregiversOf(list),
      // Deliberately unfiltered: these are keyed by an id an assignment already
      // holds, so filtering here would render a real person as "Unbekannt" in
      // colour 1 across every calendar surface rather than hide anybody.
      get: (personId: number) => byId.get(personId),
      describe: (personId: number) => {
        const person = byId.get(personId);
        // A deleted person can still appear in the changelog; the log's own
        // snapshot is preferred there, this is the last-resort fallback.
        return person ?? { name: 'Unbekannt', colorIndex: 1 };
      },
    };
  }, [persons]);
}
